<template>
    <div class="card card-default">
        <div class="card-header">API Integrations</div>
        <div class="card-body pt-0">
            <div class="row mx-0">
                <!-- Left Sidebar -->
                <div class="d-flex flex-column col-md-3 px-0 border-right py-3">
                    <div class="team-actions px-2">
                        <!-- Search Input -->
                        <div class="input-and-submit-wrapper form-group">
                            <div class="form-icon">
                                <i class="fas fa-search"></i>
                            </div>
                            <input type="text" class="has-icon form-control" v-model="searchQuery" placeholder="Search" />
                        </div>

                        <!-- Existing API Connections -->
                        <div class="teams vertical-scroll card card-body">
                            <div v-for="(connection, index) in filteredConnections" :key="index"
                                @click="selectConnection(index)"
                                class="cursor-pointer px-2 py-2 team d-flex align-items-center justify-content-between border rounded mb-2"
                                :class="{ active: selectedConnectionIndex === index }">
                                <span>{{ ucFirst(connection.connection_type) }}</span>
                                <i class="fa fa-trash text-danger" @click.stop="deleteConnection(index)"></i>
                            </div>

                            <!-- No Connections Message -->
                            <div v-if="connections.length === 0" class="text-muted text-center py-2">
                                No connections added.
                            </div>
                        </div>
                    </div>

                    <!-- Create New Connection Button -->
                    <button class="btn btn-success mt-3 mx-4" @click="createNewConnection">
                        <i class="fa fa-plus"></i> Create New
                    </button>
                </div>

                <!-- Right Panel: Connection Details -->
                <div class="col-md-9 view-team py-3 px-4">
                    <div class="card card-default" v-if="selectedConnectionIndex === null">
                        <div class="card-body text-center">
                            <p class="thin-title">Please select an API connection</p>
                        </div>
                    </div>
                    <div class="card card-default" v-else>
                        <div class="card-header">
                            {{ selectedConnection.isNew ? 'New API Connection' : 'Edit API Connection' }}
                        </div>

                        <div class="card-body">
                            <!-- Success Message -->
                            <div class="alert alert-success" v-if="form.successful">
                                API settings were saved successfully.
                            </div>

                            <form @submit.prevent="saveConnection" class="w-75 mx-auto">
                                <div class="form-group">
                                    <!-- Select API Source -->
                                    <label>Select API Source</label>
                                    <select class="form-control" v-model="selectedConnection.connection_type">
                                        <option v-for="api in availableApis" :key="api" :value="api">
                                            {{ ucFirst(api) }}
                                        </option>
                                    </select>

                                    <!-- Connection ID -->
                                    <label class="mt-3">Connection ID</label>
                                    <input type="text" class="form-control" v-model="selectedConnection.connection_id">

                                    <!-- Connection Secret -->
                                    <label class="mt-3">Connection Secret</label>
                                    <input type="password" class="form-control" v-model="selectedConnection.connection_secret">

                                    <!-- Save & Cancel Buttons -->
                                    <div class="mt-3">
                                        <button type="submit" class="btn btn-primary">
                                            {{ selectedConnection.isNew ? 'Create Connection' : 'Save Connection' }}
                                        </button>
                                        <button type="button" class="btn btn-secondary ml-2" @click="cancelEdit">
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>  
                </div>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    data() {
        return {
            availableApis: ['exclusivi', 'google', 'booking.com', 'expedia'],
            connections: [],
            selectedConnectionIndex: null,
            searchQuery: '',
            form: { successful: false },
            apiBaseUrl: '/api/integrations/' // Adjust based on your API route prefix
        };
    },
    computed: {
        selectedConnection() {
            return this.selectedConnectionIndex !== null ? this.connections[this.selectedConnectionIndex] : null;
        },
        filteredConnections() {
            if (!this.searchQuery) return this.connections;
            return this.connections.filter(conn =>
                conn.connection_type.toLowerCase().includes(this.searchQuery.toLowerCase())
            );
        }
    },
    methods: {
        async fetchConnections() {
            try {
                const response = await axios.get(`${this.apiBaseUrl}${Spark.state.currentTeam.id}`);
                if (response.data.success) {
                    this.connections = response.data.integrations;
                }
            } catch (error) {
                console.error("Failed to load integrations", error);
            }
        },
        createNewConnection() {
            this.selectedConnectionIndex = this.connections.length;
            this.connections.push({
                connection_type: this.availableApis[0],
                connection_id: '',
                connection_secret: '',
                isNew: true
            });
        },
        selectConnection(index) {
            this.selectedConnectionIndex = index;
        },
        async saveConnection() {
            const connection = this.selectedConnection;
            if (!connection.connection_id) {
                return;
            }

            try {
                const payload = {
                    connection_id: connection.connection_id,
                    connection_secret: connection.connection_secret
                };

                await axios.post(`${this.apiBaseUrl}${Spark.state.currentTeam.id}/${connection.connection_type}`, payload);
                connection.isNew = false;

                this.form.successful = true;
                setTimeout(() => (this.form.successful = false), 2000);
                await this.fetchConnections(); // Refresh list
            } catch (error) {
                console.error("Failed to save integration", error);
            }
        },
        async deleteConnection(index) {
            const connection = this.connections[index];

            // Prevent deletion confirmation for unsaved connections
            if (connection.isNew) {
                this.connections.splice(index, 1);
                this.selectedConnectionIndex = null;
                return;
            }

            swal({
                title: `Are you sure you want to remove ${connection.connection_type}?`,
                text: 'The action will be applied right away!',
                icon: 'danger',
                showCancelButton: true,
                confirmButtonColor: '#cd3537',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Remove',
            }, async (willDelete) => {
                if (willDelete) {
                    try {
                        await axios.delete(`${this.apiBaseUrl}${Spark.state.currentTeam.id}/${connection.id}`);
                        this.connections.splice(index, 1);
                        this.selectedConnectionIndex = null;
                    } catch (error) {
                        console.error("Failed to delete integration", error);
                    }
                }
            });
        },
        cancelEdit() {
            if (this.selectedConnection.isNew) {
                this.connections.pop();
            }
            this.selectedConnectionIndex = null;
        },
        ucFirst(string) {
            return string.charAt(0).toUpperCase() + string.slice(1);
        }
    },
    mounted() {
        this.fetchConnections();
    }
};
</script>

<style scoped>
.teams-row {
    height: 70vh;
    overflow-y: hidden;
    background-color: #f8f9fa;
}
.active {
    border-color: #017db9 !important;
}
</style>
